<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { useNotificationStore } from '@/stores/notificationStore'
import { NotificationType, type SiteNotification } from '@/lib/types'

/**
 * Shared toast card — used by BOTH the native `<Notification />`
 * host and the PrimeVue `<Toast>` host's `#container` slot. Keeping
 * the markup in one place ensures the two driver paths render
 * identically: same chrome, same icon mapping, same progress bar,
 * same pause-on-hover behavior.
 *
 * The card binds directly to a `SiteNotification` entry from the
 * store — it does NOT own lifetime or timer state. The store is
 * the single source of truth; this component is presentation-only.
 */
const props = defineProps<{
  entry: SiteNotification
  /**
   * Optional extra dismissal hook for the PV driver — PrimeVue's
   * `#container` slot passes a `closeCallback` we need to invoke
   * alongside `store.removeNotification` so PrimeVue's overlay
   * unmounts cleanly. Native renderer omits this.
   */
  onAfterDismiss?: () => void
}>()

const store = useNotificationStore()
const { t } = useI18n()

const iconFor = (type: NotificationType): string => {
  switch (type) {
    case NotificationType.SUCCESS:
      return 'pi pi-check-circle text-success'
    case NotificationType.ERROR:
      return 'pi pi-times-circle text-danger'
    case NotificationType.WARNING:
      return 'pi pi-exclamation-triangle text-warning'
    case NotificationType.INFO:
      return 'pi pi-info-circle text-info'
  }
}

const dismiss = (): void => {
  store.removeNotification(props.entry.id)
  props.onAfterDismiss?.()
}
</script>

<template>
  <!--
    `is-pv-translucent` flips `bg-surface-elevated` to a 90% opaque
    color-mix when the entry is rendered by the PV driver — user-
    requested glassy feel for PrimeVue toasts. Native driver stays
    fully opaque so the card stack remains visually anchored.
  -->
  <div
    class="pointer-events-auto min-w-[min(300px,calc(100vw-2rem))] max-w-[min(420px,calc(100vw-2rem))] rounded-md shadow-lg bg-surface-elevated border border-border overflow-hidden"
    :class="{ 'is-pv-translucent': entry.driver === 'pv' }"
    @mouseenter="store.pauseTimeout(entry.id)"
    @mouseleave="store.resumeTimeout(entry.id)"
  >
    <div class="flex items-start gap-3 p-3">
      <i :class="iconFor(entry.type)" class="text-xl mt-0.5" />
      <div class="flex-1 min-w-0">
        <div v-if="entry.title" class="font-semibold text-sm text-primary mb-0.5">
          {{ entry.title }}
        </div>
        <div v-if="entry.text" class="text-sm text-primary break-words">
          {{ entry.text }}
        </div>
      </div>
      <button
        type="button"
        class="text-tertiary hover:text-secondary text-xs"
        :aria-label="t('notification.dismiss')"
        @click="dismiss"
      >
        <i class="pi pi-times" />
      </button>
    </div>
    <!--
      Progress bar — pure CSS animation. `--notif-duration` is the
      entry's full timeout; `animation-play-state: paused` is
      toggled by the `paused` class which the store flips on hover.
      Driver-independent: the same animation runs whether the card
      is rendered by the native host or PrimeVue's container slot.
    -->
    <div
      v-if="entry.timeout && entry.timeout > 0"
      class="h-1 bg-surface-hover overflow-hidden"
      aria-hidden="true"
    >
      <div
        class="notif-progress h-full"
        :class="[
          {
            'bg-success': entry.type === NotificationType.SUCCESS,
            'bg-danger': entry.type === NotificationType.ERROR,
            'bg-warning': entry.type === NotificationType.WARNING,
            'bg-info': entry.type === NotificationType.INFO,
          },
          { paused: entry.paused },
        ]"
        :style="{ '--notif-duration': `${entry.timeout}ms` }"
      />
    </div>
  </div>
</template>

<style scoped lang="scss">
// PV-driver glass effect — 50% opaque card + backdrop blur for a
// frosted-glass feel. Native cards stay fully opaque (anchored
// visual). 50% opacity + 12px blur lets the underlying content
// show through clearly while the blur keeps the text readable.
//
// `-webkit-backdrop-filter` for older Safari that still gates the
// unprefixed form.
//
// Unlayered → beats Tailwind's `bg-surface-elevated` utility
// regardless of cascade order.
.is-pv-translucent {
  // PV-driver card — same tone as the native card
  // (`--theme-surface-elevated`), 70% transparent.
  //
  // backdrop-filter is INTENTIONALLY NOT applied here. The blur is
  // applied on `.p-toast-message` (see `main.scss`) — that wrapper
  // is made its own backdrop root via `isolation: isolate` so the
  // blur reads the page composite. Applying blur HERE on a nested
  // child of the wrapper would re-sample the wrapper's already-
  // painted bg (transparent) and produce a flat result.
  //
  // Border removed per user request.
  border: 0;
  background-color: color-mix(
    in srgb,
    var(--theme-surface-elevated) 50%,
    transparent
  );
}

// GPU-composited progress. Width animates from 100% → 0 over the
// notification's full timeout. `.paused` freezes the animation at
// the current frame; removing the class resumes from that frame.
.notif-progress {
  width: 100%;
  animation: notif-progress var(--notif-duration, 3000ms) linear forwards;
  transform-origin: left center;
}
.notif-progress.paused {
  animation-play-state: paused;
}

@keyframes notif-progress {
  from {
    width: 100%;
  }
  to {
    width: 0%;
  }
}
</style>
